import * as React from "react";
import { PaperProps, Box, Button, Stack, Tabs, Tab } from "@mui/material";
import {
  LocationConfiguration,
  LocationConfigurationContext,
  useLocationConfiguration,
} from "./LocationConfiguration";
import { TopNav } from "./TopNav";

import { useEffect, ReactNode, useState } from "react";
import { PreviewThemeProvider } from "./Theme";
// import { ResultsViewToggle } from "./ResultsViewToggle";
import { useLanguage } from "./Language";
import { DisplaySettings } from "../dto/DisplaySettings";

export function useDebounce<T>(value: T, delay: number) {
  const [delayValue, setDelayValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDelayValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return delayValue;
}

interface LocationConfigurationProviderProps {
  children: ReactNode;
  baseLocationConfiguration: LocationConfiguration;
  primaryColor: DisplaySettings["primaryColor"];
  secondaryColor: DisplaySettings["secondaryColor"];
  appBarColor: DisplaySettings["appBarColor"];
  mainPageLogoImageId?: DisplaySettings["mainPageLogoImageId"];
  libraryName?: LocationConfiguration["libraryName"];
  headerPosition?: DisplaySettings["headerPosition"];
}

const MockLocationConfigurationProvider: React.FC<
  LocationConfigurationProviderProps
> = ({
  children,
  baseLocationConfiguration: locationConfiguration,
  primaryColor,
  secondaryColor,
  appBarColor,
  mainPageLogoImageId,
  libraryName,
  headerPosition,
}) => {
  const contextValue = React.useMemo(() => {
    const pacSettingsCopy = { ...locationConfiguration.pacSettings };
    const displaySettingsCopy = { ...pacSettingsCopy.displaySettings };
    return {
      locationConfiguration: {
        ...locationConfiguration,
        libraryName:
          libraryName === undefined
            ? locationConfiguration.libraryName
            : libraryName,
        pacSettings: {
          ...pacSettingsCopy,
          displaySettings: {
            ...displaySettingsCopy,
            primaryColor,
            secondaryColor,
            appBarColor,
            mainPageLogoImageId:
              mainPageLogoImageId === undefined
                ? displaySettingsCopy.mainPageLogoImageId
                : mainPageLogoImageId,
            headerPosition: headerPosition,
          },
        },
      },
      saveTheme: async () => {
        return false;
      },
    };
  }, [
    locationConfiguration,
    primaryColor,
    secondaryColor,
    appBarColor,
    mainPageLogoImageId,
    libraryName,
    headerPosition,
  ]);

  return (
    <LocationConfigurationContext.Provider value={contextValue}>
      {children}
    </LocationConfigurationContext.Provider>
  );
};

interface PreviewProps extends PaperProps {
  selectedPrimaryColor: DisplaySettings["primaryColor"];
  selectedSecondaryColor: DisplaySettings["secondaryColor"];
  selectedAppBarColor: DisplaySettings["appBarColor"];
  selectedLogoImageId: DisplaySettings["mainPageLogoImageId"];
  libraryName?: LocationConfiguration["libraryName"];
  headerPosition?: DisplaySettings["headerPosition"];
}

export const ThemePreview: React.FC<PreviewProps> = ({
  selectedPrimaryColor,
  selectedSecondaryColor,
  selectedAppBarColor,
  selectedLogoImageId,
  libraryName,
  headerPosition,
  ...props
}) => {
  const { translate } = useLanguage();
  const { locationConfiguration } = useLocationConfiguration();

  useEffect(() => {
    const container = document.getElementById("no-tab-area");
    if (container) {
      const focusableElements = container.querySelectorAll(
        "button, [href], input, select, textarea, [tabindex]",
      );
      focusableElements.forEach((element) => {
        (element as HTMLElement).tabIndex = -1;
      });
    }
  }, []);

  return (
    <>
      <Box id="no-tab-area" sx={{ position: "relative" }}>
        {/* create a transluscent empty box to prevent clicking the controls in the preview */}
        <Box
          aria-label={translate("previewthemecomponent")}
          aria-hidden="true"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.0)",
            zIndex: 10,
          }}
        />
        {/* TODO we should strongly consider making this a parent of ThemePreview */}
        <MockLocationConfigurationProvider
          baseLocationConfiguration={locationConfiguration}
          primaryColor={selectedPrimaryColor}
          secondaryColor={selectedSecondaryColor}
          appBarColor={selectedAppBarColor}
          mainPageLogoImageId={selectedLogoImageId}
          libraryName={libraryName}
          headerPosition={headerPosition}
        >
          <PreviewThemeProvider isKids={false}>
            <TopNav />
            <Box
              sx={{
                p: 1,
              }}
            >
              <Stack
                spacing={2}
                direction={"column"}
                sx={{ height: "100%", width: "100%" }}
              >
                <Box sx={{ mt: 2 }}>
                  <Tabs
                    value={0}
                    indicatorColor="secondary"
                    textColor="secondary"
                    aria-label="preview tabs"
                    aria-hidden="true"
                  >
                    <Tab label={translate("booleanSearch")} />
                    <Tab label={translate("readingLevels")} />
                  </Tabs>
                </Box>
                <Box>
                  <Stack spacing={2} direction={"row"}>
                    <Button variant="contained" aria-hidden="true">
                      {translate("placeRequest")}
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      aria-hidden="true"
                    >
                      {translate("addToList")}
                    </Button>
                  </Stack>
                </Box>
                <Box>{/* <ResultsViewToggle /> */}</Box>
              </Stack>
            </Box>
          </PreviewThemeProvider>
        </MockLocationConfigurationProvider>
      </Box>
    </>
  );
};
